<template>
    <div class="grid-header-banner d-flex align-items-center justify-content-between mb-3 flex-wrap" data-test="grid-header-banner">
        <!-- Left: Icon, Title & Subtitle -->
        <div class="banner-title-area d-flex align-items-center mr-3 py-1">
            <i v-if="icon" :class="[icon, iconColor, 'mr-2 banner-icon']" aria-hidden="true"></i>
            <div>
                <div class="d-flex align-items-center flex-wrap">
                    <h4 class="mb-0 font-weight-bold banner-title">{{ title }}</h4>
                    <slot name="title-extra"></slot>
                </div>
                <small v-if="subtitle" class="text-muted banner-subtitle d-block">{{ subtitle }}</small>
            </div>
        </div>

        <!-- Right: Status Counter Pills & Actions -->
        <div class="banner-actions-area d-flex align-items-center flex-wrap mt-2 mt-sm-0 ml-auto py-1">
            <!-- Stat Pills -->
            <div class="banner-stats d-flex align-items-center flex-wrap">
                <span
                    v-for="stat in visibleStats"
                    :key="stat.key || stat.label"
                    :class="['stat-pill', 'mr-2', 'stat-pill-' + resolveVariant(stat.variant), { 'is-clickable': stat.clickable }]"
                    :data-test="stat.testId || ('stat-' + (stat.key || 'metric'))"
                    v-b-tooltip.hover.top="stat.tooltip || ''"
                    @click="onStatClick(stat)"
                >
                    <i v-if="stat.icon" :class="[stat.icon, 'mr-1 stat-pill-icon']" aria-hidden="true"></i>
                    <strong class="stat-pill-count mr-1">{{ stat.count }}</strong>
                    <span class="stat-pill-label">{{ stat.label }}</span>
                </span>
                <slot name="custom-stats"></slot>
            </div>

            <!-- Action Slot (e.g. Mark All Read) -->
            <div v-if="$slots.actions || $slots.actions" class="banner-action-buttons d-flex align-items-center ml-2">
                <slot name="actions"></slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'grid-header-banner',
    props: {
        title: {
            type: String,
            required: true
        },
        subtitle: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: ''
        },
        iconColor: {
            type: String,
            default: 'text-primary'
        },
        stats: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        visibleStats() {
            return (this.stats || []).filter(stat => {
                if (stat.visible === false) return false;
                const count = Number(stat.count);
                if (stat.showZero) return true;
                return !isNaN(count) ? count > 0 : !!stat.count;
            });
        }
    },
    methods: {
        resolveVariant(variant) {
            const map = {
                danger: 'rose',
                warning: 'amber',
                primary: 'sky',
                info: 'sky',
                success: 'emerald',
                secondary: 'slate',
                dark: 'slate',
                light: 'slate'
            };
            return map[variant] || variant || 'slate';
        },
        onStatClick(stat) {
            if (stat.clickable) {
                this.$emit('stat-click', stat);
            }
        }
    }
};
</script>

<style scoped>
.grid-header-banner {
    min-height: 2.75rem;
}

.banner-icon {
    font-size: 1.75rem;
    line-height: 1;
}

.banner-title {
    font-size: 1.35rem;
    line-height: 1.25;
    letter-spacing: -0.015em;
    color: var(--vt-text, #1e293b);
}

.banner-subtitle {
    font-size: 0.8125rem;
    line-height: 1.25;
    color: var(--vt-muted, #64748b);
}

/* Stat Pill Base */
.stat-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.32rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    user-select: none;
    transition: all 0.15s ease-in-out;
}

.stat-pill-icon {
    font-size: 0.85rem;
}

.stat-pill-count {
    font-weight: 700;
}

.stat-pill.is-clickable {
    cursor: pointer;
}

.stat-pill.is-clickable:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
}

/* Forgiving Color Variants - Light Theme */
.stat-pill-rose {
    background-color: #fff1f2;
    color: #e11d48;
    border: 1px solid #fecdd3;
}

.stat-pill-amber {
    background-color: #fffbeb;
    color: #b45309;
    border: 1px solid #fde68a;
}

.stat-pill-sky {
    background-color: #f0f9ff;
    color: #0284c7;
    border: 1px solid #bae6fd;
}

.stat-pill-emerald {
    background-color: #ecfdf5;
    color: #059669;
    border: 1px solid #a7f3d0;
}

.stat-pill-violet {
    background-color: #f5f3ff;
    color: #7c3aed;
    border: 1px solid #ddd6fe;
}

.stat-pill-slate {
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
}

/* Forgiving Color Variants - Dark Theme */
[data-theme="dark"] .banner-title {
    color: #f8fafc;
}

[data-theme="dark"] .banner-subtitle {
    color: #94a3b8;
}

[data-theme="dark"] .stat-pill-rose {
    background-color: rgba(244, 63, 94, 0.16);
    color: #fb7185;
    border: 1px solid rgba(244, 63, 94, 0.35);
}

[data-theme="dark"] .stat-pill-amber {
    background-color: rgba(245, 158, 11, 0.16);
    color: #fbbf24;
    border: 1px solid rgba(245, 158, 11, 0.35);
}

[data-theme="dark"] .stat-pill-sky {
    background-color: rgba(14, 165, 233, 0.16);
    color: #38bdf8;
    border: 1px solid rgba(14, 165, 233, 0.35);
}

[data-theme="dark"] .stat-pill-emerald {
    background-color: rgba(16, 185, 129, 0.16);
    color: #34d399;
    border: 1px solid rgba(16, 185, 129, 0.35);
}

[data-theme="dark"] .stat-pill-violet {
    background-color: rgba(139, 92, 246, 0.16);
    color: #a78bfa;
    border: 1px solid rgba(139, 92, 246, 0.35);
}

[data-theme="dark"] .stat-pill-slate {
    background-color: rgba(148, 163, 184, 0.15);
    color: #cbd5e1;
    border: 1px solid rgba(148, 163, 184, 0.25);
}

@media (max-width: 991.98px) {
    .grid-header-banner {
        min-height: auto;
        margin-bottom: 0.5rem !important;
        gap: 0.25rem;
    }

    .banner-icon {
        font-size: 1.35rem;
    }

    .banner-title {
        font-size: 1.08rem;
    }

    .banner-subtitle {
        display: none !important;
    }

    .banner-actions-area {
        margin-top: 0 !important;
        gap: 0.25rem;
    }

    .stat-pill {
        padding: 0.22rem 0.55rem;
        font-size: 0.74rem;
        margin-right: 0.35rem !important;
    }
}
</style>
